import { CategoryDataType } from "@/api/CategoryApi";
import { ProductDataType } from "@/api/ProductsApi";
import { isValidCabysCode, normalizeCabysCode, productTypeFromCabys } from "@utils/Cabys";
import { roundCant } from "@utils/Cant";
import { EDOCUMENT_TEXT_MAX_LENGTH, sanitizeSafeText } from "@amplifyShared/safeText";
import { IVA_TAX_TYPE, TAXES_PERCENTAGES_VALUES } from "@utils/Constants";
import { collectMissingLookupNames, nextAvailableCode as nextCodeFrom, resolveRows } from "./core/resolveRows";
import { DuplicatePolicy, ResolveResult, ResolvedRow } from "./core/types";
import { ProductColumnMapping, mapRowToProduct } from "./productImportMapping";

/** Unidad de medida que se asume cuando el archivo no trae la columna. */
export const DEFAULT_MEASURE_UNIT = "Unid";

/** Tarifa general del 13%, la que aplica a casi todo lo que se importa. */
export const DEFAULT_TAX_PERCENTAGE_CODE = "08";

/** "Global": el producto se ve tanto en el punto de venta como en facturacion. */
export const DEFAULT_PRODUCT_VISIBILITY = 1;

export type ProductResolvedRow = ResolvedRow<ProductDataType>;

/** Categoria que el archivo menciona y el tenant todavia no tiene. */
export type MissingCategory = {
    nombre: string;
    /** Se hereda del primer producto del archivo que cae en esta categoria. */
    codigo_cabys: string;
    tipo_producto?: number;
    tipo_impuesto?: string;
    porcentaje_impuesto?: string;
    unidad_medida?: string;
};

export type ProductResolveResult = ResolveResult<ProductDataType> & {
    missingCategories: MissingCategory[];
    /**
     * Atributos que cada fila trajo del archivo, sin contar los defaults. Al actualizar un
     * producto existente solo se mandan estos, para no pisar con ceros lo que ya tenia.
     */
    providedFields: Map<number, Set<string>>;
};

export const normalizeCategoryName = (raw?: string | null): string => `${raw ?? ""}`.trim().toUpperCase();

/** Indice de categorias existentes por nombre normalizado. */
export const buildCategoryIndex = (categories: CategoryDataType[]): Map<string, CategoryDataType> => {
    const index = new Map<string, CategoryDataType>();
    categories.forEach((category) => {
        const key = normalizeCategoryName(category.nombre);
        if (key && category.id && !index.has(key)) index.set(key, category);
    });
    return index;
};

/** Siguiente codigo consecutivo disponible entre los registros ya existentes. */
export const nextAvailableCode = (items: Array<{ codigo?: unknown }>): number =>
    nextCodeFrom(items.map((item) => item.codigo as string | number | null | undefined));

const isZeroTaxPercentage = (code?: string | null): boolean =>
    TAXES_PERCENTAGES_VALUES[`${code ?? ""}`] === "0";

/**
 * Categorias del archivo que todavia no existen, con los datos que hereda cada una.
 *
 * Se calcula en una pasada previa a resolver las filas para que todos los productos de una
 * categoria nueva puedan heredar su CAByS, sin importar si el primero del archivo lo traia.
 */
const planMissingCategories = (
    dataRows: string[][],
    mapping: ProductColumnMapping,
    categoryIndex: Map<string, CategoryDataType>
): Map<string, MissingCategory> => {
    const planned = new Map<string, MissingCategory>();

    dataRows.forEach((row) => {
        const { product, categoryName } = mapRowToProduct(row, mapping);
        const name = categoryName.trim();
        if (!name) return;

        const key = normalizeCategoryName(name);
        if (categoryIndex.has(key)) return;

        const current = planned.get(key) || { nombre: name, codigo_cabys: "" };

        const cabys = normalizeCabysCode(product.codigo_cabys);
        if (!current.codigo_cabys && isValidCabysCode(cabys)) {
            current.codigo_cabys = cabys;
            // El tipo de producto acompana al CAByS del que se dedujo.
            current.tipo_producto = product.tipo_producto ?? productTypeFromCabys(cabys);
        }
        if (!current.tipo_producto && product.tipo_producto) current.tipo_producto = product.tipo_producto;
        if (!current.tipo_impuesto && product.tipo_impuesto) current.tipo_impuesto = product.tipo_impuesto;
        if (!current.porcentaje_impuesto && product.porcentaje_impuesto) {
            current.porcentaje_impuesto = product.porcentaje_impuesto;
        }
        if (!current.unidad_medida && product.unidad_medida) current.unidad_medida = product.unidad_medida;

        planned.set(key, current);
    });

    return planned;
};

type CategoryDefaults = {
    nombre?: string | null;
    codigo_cabys?: string | null;
    sin_inventario?: boolean | null;
};

/**
 * Completa el producto con los valores que el archivo no trae.
 *
 * Devuelve el motivo por el que la fila no se puede importar, o `null` si quedo lista.
 */
export const applyProductDefaults = (
    product: ProductDataType,
    category: CategoryDefaults | undefined,
    mappedFields: Set<string>,
    messages: string[]
): string | null => {
    const cabys = normalizeCabysCode(product.codigo_cabys);

    if (isValidCabysCode(cabys)) {
        product.codigo_cabys = cabys;
    } else {
        const categoryCabys = normalizeCabysCode(category?.codigo_cabys);
        if (!isValidCabysCode(categoryCabys)) {
            return "La fila no tiene un codigo CAByS valido y su categoria tampoco";
        }
        product.codigo_cabys = categoryCabys;
        messages.push("Sin CAByS propio: se usa el de la categoria");
    }

    if (!`${product.descripcion_fact ?? ""}`.trim()) {
        const categoryName = `${category?.nombre ?? ""}`.trim();
        if (!categoryName) {
            return "La fila no tiene descripcion de factura ni categoria de la que tomarla";
        }
        product.descripcion_fact = categoryName;
        messages.push("Sin descripcion de factura: se usa el nombre de la categoria");
    }

    // La descripcion de factura acaba en el nodo <Detalle> del XML y en el payload que
    // viaja al Step Function, asi que un archivo externo no puede meter al catalogo un
    // caracter que luego impida enviar las facturas de ese producto.
    const detalle = sanitizeSafeText(product.descripcion_fact, EDOCUMENT_TEXT_MAX_LENGTH);
    if (detalle !== product.descripcion_fact) {
        product.descripcion_fact = detalle;
        messages.push("La descripcion de factura se ajusto por caracteres no admitidos");
    }
    if (product.descripcion !== undefined && product.descripcion !== null) {
        product.descripcion = sanitizeSafeText(product.descripcion);
    }

    if (!product.unidad_medida) product.unidad_medida = DEFAULT_MEASURE_UNIT;
    // El tipo de impuesto de un producto importado siempre es IVA.
    if (!product.tipo_impuesto) product.tipo_impuesto = IVA_TAX_TYPE;
    if (!product.porcentaje_impuesto) product.porcentaje_impuesto = DEFAULT_TAX_PERCENTAGE_CODE;

    if (product.tipo_producto === undefined || product.tipo_producto === null) {
        product.tipo_producto = productTypeFromCabys(product.codigo_cabys);
    }

    // Sin columna propia, la exencion se deduce de la tarifa: 0% es exento.
    if (!mappedFields.has("exento")) {
        product.exento = isZeroTaxPercentage(product.porcentaje_impuesto);
    }

    if (product.activo === undefined || product.activo === null) product.activo = true;
    if (product.visibilidad === undefined || product.visibilidad === null) {
        product.visibilidad = DEFAULT_PRODUCT_VISIBILITY;
    }

    product.costo = Number(product.costo ?? 0);
    product.minimo = roundCant(Number(product.minimo ?? 0));
    product.maximo = roundCant(Number(product.maximo ?? 0));

    // Una categoria sin inventario no lleva existencias, aunque el archivo traiga un numero.
    const fileStock = roundCant(Number(product.existencias ?? 0));
    if (category?.sin_inventario) {
        if (fileStock !== 0) messages.push("La categoria no maneja inventario: las existencias quedan en 0");
        product.existencias = 0;
    } else {
        product.existencias = fileStock;
    }

    return null;
};

/**
 * Transforma las filas del archivo en productos listos para importar: normaliza los
 * valores, resuelve las categorias, aplica los defaults del modelo, detecta duplicados
 * contra lo que ya existe en el tenant y asigna los codigos que falten.
 */
export const resolveProductRows = (params: {
    dataRows: string[][];
    mapping: ProductColumnMapping;
    existingProducts: ProductDataType[];
    existingCategories: CategoryDataType[];
    policy: DuplicatePolicy;
}): ProductResolveResult => {
    const { dataRows, mapping, existingProducts, existingCategories, policy } = params;

    const categoryIndex = buildCategoryIndex(existingCategories);
    const plannedCategories = planMissingCategories(dataRows, mapping, categoryIndex);
    const mappedFields = new Set(Object.values(mapping).filter((field): field is string => !!field));
    const providedFields = new Map<number, Set<string>>();

    const { rows } = resolveRows<ProductDataType, ProductDataType>({
        dataRows,
        mapping,
        policy,
        existing: existingProducts,
        entitySingular: "producto",

        mapRow: (row, columnMapping) => {
            const { product, categoryName } = mapRowToProduct(row, columnMapping as ProductColumnMapping);
            return { data: product, lookupName: categoryName };
        },

        prepare: (product, categoryName, rowIndex) => {
            // Lo que vino del archivo, antes de los defaults: es lo unico que se manda al
            // actualizar un producto que ya existe.
            providedFields.set(rowIndex, new Set(Object.keys(product)));

            const messages: string[] = [];
            const key = normalizeCategoryName(categoryName);

            const existingCategory = key ? categoryIndex.get(key) : undefined;
            if (existingCategory?.id) product.categoryId = existingCategory.id;
            // La categoria salio de una columna del archivo, asi que tambien se actualiza
            // en los productos que ya existen (su id puede llegar recien creada).
            if (key) providedFields.get(rowIndex)?.add("categoryId");

            const category: CategoryDefaults | undefined =
                existingCategory || (key ? plannedCategories.get(key) : undefined);

            const error = applyProductDefaults(product, category, mappedFields, messages);
            return { error, messages };
        },

        duplicateKeys: (product) => [codeKey(product.codigo), barcodeKey(product.codigobarras)],
        existingKeys: (product) => [codeKey(product.codigo), barcodeKey(product.codigobarras)],
        idOf: (product) => product.id,

        codeOf: (product) => `${product.codigo ?? ""}`,
        setCode: (product, code) => {
            product.codigo = code;
        },
        existingCodeOf: (product) => `${product.codigo ?? ""}`,

        onDuplicateAsNew: (product, messages) => {
            // El codigo de barras identifica al producto original: no se puede repetir.
            if (product.codigobarras) {
                delete product.codigobarras;
                messages.push("Se importa sin codigo de barras para no repetir el del producto existente");
            }
        },
    });

    const missingNames = collectMissingLookupNames(rows, (row) => !!row.data.categoryId);
    const missingCategories = missingNames
        .map((name) => plannedCategories.get(normalizeCategoryName(name)))
        .filter((category): category is MissingCategory => !!category);

    return { rows, missingCategories, providedFields };
};

const codeKey = (codigo?: unknown): string => {
    const value = `${codigo ?? ""}`.trim();
    return value ? `codigo:${value}` : "";
};

const barcodeKey = (codigobarras?: unknown): string => {
    const value = `${codigobarras ?? ""}`.trim();
    return value ? `barras:${value}` : "";
};

/** Rellena `categoryId` en las filas cuya categoria se acaba de crear. */
export const applyCreatedCategories = (
    rows: ProductResolvedRow[],
    categoryIndex: Map<string, CategoryDataType>
): ProductResolvedRow[] =>
    rows.map((row) => {
        if (!row.lookupName || row.data.categoryId) return row;

        const category = categoryIndex.get(normalizeCategoryName(row.lookupName));
        return category?.id ? { ...row, data: { ...row.data, categoryId: category.id } } : row;
    });
